/* eslint-disable no-console */
import { useMemo } from "react";
import {
  ApolloClient,
  ApolloLink,
  from,
  HttpLink,
  InMemoryCache,
  split,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import merge from "deepmerge";
import isEqual from "lodash/isEqual";
import configs from "../common/config";
import { GET_REGIONS, purgeRegionsCache } from "../graphql/queries/regions";
import { TYPES_KEYFIELD_MAP } from "../common/constants";
import { getDeviceID } from "./helpers";
import pkg from "../../package.json";
import { getClientCookie } from "./cookiesHelpers";
import Config from "@/common/config";

const noop = () => {};
const __CACHED_OPERATIONS = {
  getAllCities: "?cached=true",
  getAllCategories: "?cached=true",
  getAllDistricts: "?cached=true",
  getAllDirections: "?cached=true",
  GetListingComments: "?cached=true",
};
const CACHED_OPERATIONS = {};
export const APOLLO_STATE_PROP_NAME = "__APOLLO_STATE__";

let apolloClient;

const {
  CityType: CityTypeKeyFields,
  DirectionType: DirectionTypeKeyFields,
  DistrictType: DistrictTypeKeyFields,
} = TYPES_KEYFIELD_MAP;

const logOperation = (operation) => {
  console.log(`[Operation Name]: ${operation.operationName}`);
  console.log(`[Operation query]: ${JSON.stringify(operation.query)}`);
  console.log(`[Operation variables]: ${JSON.stringify(operation.variables)}`);
};

// FIXME: these logs are temporary to debug prod server. remove them ASAP. 23/07/2022
function createErrorLink(errorCallback) {
  errorCallback = errorCallback || noop;
  return onError(({ networkError, graphQLErrors, operation }) => {
    if (graphQLErrors) {
      console.log(`[Graphql error]: ${JSON.stringify(graphQLErrors)}`);
      logOperation(operation);

      //if (typeof window !== "undefined")
      //  Toast(graphQLErrors.map((e) => e.message).join("\n"));
    }

    if (networkError) {
      console.log(
        `[Network error]: ${JSON.stringify(networkError)} | ${
          operation.operationName
        }`,
      );
      logOperation(operation);
      if (networkError.result?.AuthError === "Invalid Token") {
        errorCallback();
      }
    }
  });
}

const authMiddleware = (errorCallback) =>
  new ApolloLink((operation, forward) => {
    const ssrMode = typeof window === "undefined";
    const { token, aqarPlus } = operation.getContext();

    const FIXED_HEADERS = {
      "req-app": Config.SOURCE_HEADER,
      "req-device-token": getDeviceID(),
      "app-version": pkg.version,
    };
    // This will grab the token only on CSR. It is not an issue for now because aqar+ filters are not handled on ssr. 21/05/2023
    const aqarPlusToken = aqarPlus || getClientCookie("webapp_token");

    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        ...FIXED_HEADERS,
        ...(!!aqarPlusToken && !token && { token: aqarPlusToken }),
      },
    }));
    if (token) {
      operation.setContext(({ headers = {} }) => ({
        headers: {
          ...headers,
          ...FIXED_HEADERS,

          // We need to send the token in the header for all public queries for loggedin users
        },
        // if we have token in the context we change the ep to be /auth-graphql
        ...(!!token && {
          uri: `${
            ssrMode
              ? process.env.NEXT_PUBLIC_GRAPHQL_SSR_AUTH_EP
              : configs.GQL_AUTH_EP
          }?token=${token}`,
        }),
      }));
    }
    if (!ssrMode && CACHED_OPERATIONS[operation.operationName]) {
      /* CACHED has top be public endpoint */
      operation.setContext(({ headers = {} }) => ({
        headers: {
          ...headers,
          ...FIXED_HEADERS,
          "Content-Type": "*/*",
        },

        uri: `${configs.GQL_EP}${
          CACHED_OPERATIONS[operation.operationName]
        }&operation=${operation.operationName}`,
      }));
    }

    return forward(operation).map((response) => {
      if (!response.errors?.length) return response;

      if (
        response.errors[0]?.message?.includes("VPN") &&
        typeof window !== "undefined"
      ) {
        errorCallback({
          children: <div>{response.errors[0].message}</div>,
          okLabel: "حسنا",
          onOk: () => {
            errorCallback({ visible: false });
          },
          onCancel: () => {
            errorCallback({ visible: false });
          },
        });
      }
      return response;
    });
  });

const httpLink = split(
  (op) => {
    const ssrMode = typeof window === "undefined";
    return !ssrMode && CACHED_OPERATIONS[op.operationName];
  },
  new HttpLink({
    useGETForQueries: true,
    headers: { "Content-Type": "*/*" },
    uri:
      typeof window === "undefined"
        ? process.env.NEXT_PUBLIC_GRAPHQL_SSR_EP
        : configs.GQL_EP,
  }),
  new HttpLink({
    uri:
      typeof window === "undefined"
        ? process.env.NEXT_PUBLIC_GRAPHQL_SSR_EP
        : configs.GQL_EP,
  }),
);
function createApolloClient(errorCallback) {
  return new ApolloClient({
    ssrMode: typeof window === "undefined",
    link: from([
      createErrorLink(errorCallback),
      authMiddleware(errorCallback),
      httpLink,
    ]),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            Web: {
              merge(existing, incoming, { mergeObjects }) {
                return mergeObjects(existing, incoming);
              },
            },
            Listing: {
              merge(existing, incoming, { mergeObjects }) {
                return mergeObjects(existing, incoming);
              },
            },
            Search: {
              merge(existing, incoming, { mergeObjects }) {
                return mergeObjects(existing, incoming);
              },
            },
            RealEstateProjects: {
              merge(existing, incoming, { mergeObjects }) {
                return mergeObjects(existing, incoming);
              },
            },
          },
        },
        Category: {
          keyFields: ["uri"],
        },
        DistrictType: {
          keyFields: [DistrictTypeKeyFields],
        },
        DirectionType: {
          keyFields: [DirectionTypeKeyFields],
        },
        CityType: {
          keyFields: [CityTypeKeyFields],
        },
        City: {
          keyFields: ["city_id", "path"],
        },
        Direction: {
          keyFields: ["direction_id", "path"],
        },
        District: {
          keyFields: ["district_id", "path"],
        },
        WebListing: {
          keyFields: ["uri"],
        },
        RealEstatesProject: {
          keyFields: ["id"],
        },
      },
    }),
  });
}

export function initApolloClient(initialState = null, errorCallback = noop) {
  const _apolloClient = apolloClient ?? createApolloClient(errorCallback);

  // If we have client side cache and server side cache, merge them together
  if (initialState) {
    const existingCache = _apolloClient.extract();

    const data = merge(initialState, existingCache, {
      arrayMerge: (destinationArray, sourceArray) => [
        ...sourceArray,
        ...destinationArray.filter((d) =>
          sourceArray.every((s) => !isEqual(d, s)),
        ),
      ],
    });

    _apolloClient.cache.restore(data);
  }

  // For SSG and SSR always create a new Apollo Client so that data isn't shared between connections
  if (typeof window === "undefined") return _apolloClient;
  // Create the Apollo Client once in the client
  if (!apolloClient) apolloClient = _apolloClient;

  return _apolloClient;
}

export function addApolloState(client, pageProps) {
  const regionsCachedQuery = client.readQuery({ query: GET_REGIONS });
  if (regionsCachedQuery) {
    purgeRegionsCache(client);
  }
  if (pageProps?.props) {
    pageProps.props[APOLLO_STATE_PROP_NAME] = client.cache.extract();
  }

  return pageProps;
}
export function dehydrateApolloClient(client, pageProps) {
  if (pageProps) {
    pageProps[APOLLO_STATE_PROP_NAME] = client.cache.extract();
  }

  return pageProps;
}
export function useApollo(pageProps, handleError) {
  const state = pageProps[APOLLO_STATE_PROP_NAME];
  return useMemo(() => initApolloClient(state, handleError), [state]);
}

export const getMaintainedParam = (cooperative) => {
  if (typeof cooperative === "undefined" || cooperative === true) return {};
  if (cooperative === null || cooperative === false)
    return { maintained: { eq: 1 } };
};
